import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../redux/redux.hook";
import {
  closeStory,
  nextItem,
  nextStory,
  openStory,
  prevItem,
  prevStory,
  setProgress,
  resetProgress,
  pauseStory,
  resumeStory,
  setStories,
} from "./story.slice";
import { HomeStory } from "../home/home.types";

const DEFAULT_DURATION = 5000;
const UPDATE_INTERVAL = 50;

interface StoryNavigation {
  canGoPrev: boolean;
  canGoNext: boolean;
  isFirstItem: boolean;
  isLastItem: boolean;
  isFirstStory: boolean;
  isLastStory: boolean;
}

/**
 * Story şeridini Redux'a yazar. Dispatch-only.
 *
 * useStory() tüm `state.story` slice'ına abone OLMAKLA KALMIYOR, üç effect de
 * kuruyor: 50 ms'lik progress interval'i, keydown dinleyicisi ve body scroll
 * kilidi. Bunlar yalnızca açık story görüntüleyicisine (StoryContent) ait.
 * Şeritteki her StoryCard useStory() çağırdığı için ana sayfada 13 kart + swiper
 * = 14 kopya effect kuruluyordu; story açıldığında 14 ayrı 50 ms interval ve 14
 * keydown dinleyicisi devreye giriyordu.
 */
export const useUpdateStories = () => {
  const dispatch = useAppDispatch();
  return useCallback(
    (newStories: HomeStory[]) => {
      dispatch(setStories(newStories));
    },
    [dispatch],
  );
};

/** Story görüntüleyicisini açar — tek primitif selector (`stories.length`). */
export const useOpenStory = () => {
  const dispatch = useAppDispatch();
  const storyCount = useAppSelector((state) => state.story.stories.length);

  return useCallback(
    (index: number) => {
      if (index >= 0 && index < storyCount) {
        dispatch(openStory(index));
      }
    },
    [dispatch, storyCount],
  );
};

export const useStory = () => {
  const dispatch = useAppDispatch();
  const storyState = useAppSelector((state) => state.story);

  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const stories = storyState.stories;

  const updateStories = useCallback(
    (newStories: HomeStory[]) => {
      dispatch(setStories(newStories));
    },
    [dispatch],
  );

  const currentStory = useMemo(() => {
    if (
      !storyState.isOpen ||
      stories.length === 0 ||
      storyState.currentStoryIndex < 0
    ) {
      return null;
    }
    return stories[storyState.currentStoryIndex] ?? null;
  }, [storyState.isOpen, storyState.currentStoryIndex, stories]);

  const currentItem = useMemo(() => {
    if (!currentStory) return null;
    return currentStory.items[storyState.currentItemIndex] ?? null;
  }, [currentStory, storyState.currentItemIndex]);

  const navigation = useMemo<StoryNavigation>(() => {
    const isFirstItem = storyState.currentItemIndex === 0;
    const isLastItem = currentStory
      ? storyState.currentItemIndex === currentStory.items.length - 1
      : true;
    const isFirstStory = storyState.currentStoryIndex === 0;
    const isLastStory = storyState.currentStoryIndex === stories.length - 1;

    return {
      canGoPrev: !isFirstItem || !isFirstStory,
      canGoNext: !isLastItem || !isLastStory,
      isFirstItem,
      isLastItem,
      isFirstStory,
      isLastStory,
    };
  }, [
    storyState.currentItemIndex,
    storyState.currentStoryIndex,
    currentStory,
    stories.length,
  ]);

  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const handleNext = useCallback(() => {
    clearProgressInterval();

    if (!currentItem || !currentStory) {
      dispatch(closeStory());
      return;
    }

    if (!navigation.isLastItem) {
      dispatch(nextItem());
    } else if (!navigation.isLastStory) {
      dispatch(nextStory());
    } else {
      dispatch(closeStory());
    }
  }, [
    dispatch,
    currentItem,
    currentStory,
    navigation.isLastItem,
    navigation.isLastStory,
    clearProgressInterval,
  ]);

  const handlePrev = useCallback(() => {
    clearProgressInterval();

    if (!navigation.isFirstItem) {
      dispatch(prevItem());
    } else if (!navigation.isFirstStory) {
      dispatch(prevStory());
    }
  }, [
    dispatch,
    navigation.isFirstItem,
    navigation.isFirstStory,
    clearProgressInterval,
  ]);

  const startProgress = useCallback(() => {
    if (!currentItem || !storyState.isPlaying || storyState.isPaused) {
      return;
    }

    clearProgressInterval();

    const duration =
      currentItem.type === "video"
        ? currentItem.video_duration * 1000
        : DEFAULT_DURATION;

    const elapsedFromProgress = (storyState.progress / 100) * duration;
    startTimeRef.current = performance.now() - elapsedFromProgress;

    progressIntervalRef.current = setInterval(() => {
      const elapsed = performance.now() - startTimeRef.current;
      const progressPercent = (elapsed / duration) * 100;

      if (progressPercent >= 100) {
        clearProgressInterval();
        handleNext();
      } else {
        dispatch(setProgress(progressPercent));
      }
    }, UPDATE_INTERVAL);
  }, [
    currentItem,
    storyState.isPlaying,
    storyState.isPaused,
    storyState.progress,
    dispatch,
    clearProgressInterval,
    handleNext,
  ]);

  const handleOpen = useCallback(
    (index: number) => {
      if (index >= 0 && index < stories.length) {
        dispatch(openStory(index));
      }
    },
    [dispatch, stories.length],
  );

  const handleClose = useCallback(() => {
    clearProgressInterval();
    dispatch(closeStory());
  }, [dispatch, clearProgressInterval]);

  /** Sadece timer'ı durdurur — modal açık kalır, pause state değişmez.
   *  Story butonuna tıklanıp başka sayfaya navigasyon yapılırken kullanılır. */
  const handleStopProgress = useCallback(() => {
    clearProgressInterval();
  }, [clearProgressInterval]);

  const handlePause = useCallback(() => {
    if (storyState.isPlaying && !storyState.isPaused) {
      clearProgressInterval();
      dispatch(pauseStory());
    }
  }, [
    dispatch,
    storyState.isPlaying,
    storyState.isPaused,
    clearProgressInterval,
  ]);

  const handleResume = useCallback(() => {
    if (storyState.isPaused) {
      dispatch(resumeStory());
    }
  }, [dispatch, storyState.isPaused]);

  useEffect(() => {
    if (storyState.isOpen && storyState.isPlaying && !storyState.isPaused) {
      startProgress();
    } else {
      clearProgressInterval();
    }

    return () => {
      clearProgressInterval();
    };
  }, [
    storyState.isOpen,
    storyState.isPlaying,
    storyState.isPaused,
    storyState.currentItemIndex,
    storyState.currentStoryIndex,
    startProgress,
    clearProgressInterval,
  ]);

  useEffect(() => {
    if (storyState.isOpen) {
      dispatch(resetProgress());
    }
  }, [
    storyState.currentItemIndex,
    storyState.currentStoryIndex,
    dispatch,
    storyState.isOpen,
  ]);

  useEffect(() => {
    if (!storyState.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;

      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          if (navigation.canGoNext) {
            handleNext();
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (navigation.canGoPrev) {
            handlePrev();
          }
          break;
        case " ":
          e.preventDefault();
          if (storyState.isPaused) {
            handleResume();
          } else {
            handlePause();
          }
          break;
        case "Escape":
          e.preventDefault();
          handleClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [
    storyState.isOpen,
    storyState.isPaused,
    navigation.canGoNext,
    navigation.canGoPrev,
    handleNext,
    handlePrev,
    handlePause,
    handleResume,
    handleClose,
  ]);

  return {
    isOpen: storyState.isOpen,
    currentStoryIndex: storyState.currentStoryIndex,
    currentItemIndex: storyState.currentItemIndex,
    isPlaying: storyState.isPlaying,
    isPaused: storyState.isPaused,
    progress: storyState.progress,
    stories,
    currentStory,
    currentItem,
    ...navigation,
    handleNext,
    handlePrev,
    handleOpen,
    handleClose,
    handlePause,
    handleResume,
    handleStopProgress,
    updateStories,
    videoRef,
  };
};
